// The browser's offline audio: Cache Storage, fetch with the session cookie,
// navigator.onLine, and Web Locks so tabs sharing one cache take turns.

import type { Capture } from "@aybuben/client/analytics/events.ts";
import {
	createOfflineAudio,
	type Lock,
	type OfflineAudio,
} from "@aybuben/client/offline/controller.ts";
import { downloadAudio } from "@aybuben/client/offline/download.ts";

import { AudioCache, CacheAudioStore } from "../audio-cache.ts";

/** A captive portal or a dead Wi-Fi hangs a request; this turns it into a network failure. */
export const DOWNLOAD_TIMEOUT_MS = 8000;

const tabLock: Lock = (fn) => {
	const locks = globalThis.navigator?.locks;
	return locks ? locks.request("aybuben-audio-sync", () => fn()) : fn();
};

export function createWebOfflineAudio(capture?: Capture): OfflineAudio {
	const cache = new AudioCache();
	// Under `bun test` there is no backend to download from, and the DOM tests
	// must not start network passes. Without Cache Storage nothing can be kept.
	const store =
		process.env.NODE_ENV === "test" || !cache.available ? null : new CacheAudioStore(cache);
	return createOfflineAudio({
		store,
		download: (a) =>
			downloadAudio(
				a,
				(url, signal) => fetch(url, { credentials: "include", signal }),
				DOWNLOAD_TIMEOUT_MS,
			),
		isOnline: () => typeof navigator === "undefined" || navigator.onLine,
		now: Date.now,
		capture,
		lock: tabLock,
	});
}

/**
 * Asks the browser not to evict the recordings. WebKit grants it to home-screen
 * apps and evicts everything else after seven days without a visit; a denial
 * changes nothing, because every lesson checks its recordings before opening.
 */
export function requestPersistence(): void {
	const standalone =
		globalThis.matchMedia?.("(display-mode: standalone)").matches ||
		// iOS Safari's own flag, absent from the DOM types.
		(globalThis.navigator as { standalone?: boolean } | undefined)?.standalone === true;
	if (standalone) void globalThis.navigator?.storage?.persist?.().catch(() => false);
}
